<script setup lang="ts">
import { UkTouhouEvent } from '../classes/UkTouhouEvent';

const props = defineProps<{
  event: UkTouhouEvent;
}>();

const descriptionParagraphs = props.event.description.split('\n\n');

const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const weekday = (date: Date) => weekdays[date.getDay()];

const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const month = (date: Date) => months[date.getMonth()];
</script>

<template>
  <div class="event-card" :id="event.id">
    <!-- Header Section: Title + Date Square -->
    <div class="card-header">
      <h4>
        {{ event.name }}
        <a class="event-anchor" :href="'#' + event.id">link to event</a>
      </h4>

      <div class="calendar-date-range">
        <div class="calendar-date">
          <div class="weekday">{{ weekday(event.begin) }}</div>
          <div class="date">{{ event.begin.getDate() }}</div>
          <div class="month">{{ month(event.begin) }}</div>
        </div>

        <template v-if="event.isMultiDay">
          <div class="arrow">-></div>
          <div class="calendar-date">
            <div class="weekday">{{ weekday(event.end) }}</div>
            <div class="date">{{ event.end.getDate() }}</div>
            <div class="month">{{ month(event.end) }}</div>
          </div>
        </template>
      </div>
    </div>

    <!-- Banner Section -->
    <div class="card-banner" v-if="event.banner">
      <img :src="'/event-banners/' + event.banner.imageUrl" :alt="event.name"/>
      <div class="credit" v-if="event.banner.creditName">
        Banner art by
        <a v-if="event.banner.creditUrl" :href="event.banner.creditUrl" target="_blank">{{ event.banner.creditName }}</a>
        <template v-else>{{ event.banner.creditName }}</template>
      </div>
    </div>

    <!-- Meta Section: Date, Location, Website -->
    <div class="card-meta">
      <div class="meta-item">
        <span class="meta-icon">🗓️</span>
        <span class="meta-text">{{ event.dateToString }}</span>
      </div>
      <div class="meta-item">
        <span class="meta-icon">📍</span>
        <span class="meta-text">{{ event.locationOneLine }}</span>
      </div>
      <div v-if="event.website" class="meta-item">
        <span class="meta-icon">🌐</span>
        <a class="meta-link" :href="event.website" target="_blank">{{ event.website }}</a>
      </div>
    </div>

    <!-- Body Section: Description -->
    <div class="card-body" v-if="descriptionParagraphs.length > 0">
      <p v-for="(paragraph, index) in descriptionParagraphs" :key="index">{{ paragraph }}</p>
    </div>
  </div>
</template>

<style scoped>
.event-card {
  margin-bottom: var(--space-lg);
  border: 1px solid var(--color-border-primary);
}

.card-header {
  border-bottom: 1px solid var(--color-border-subtle);
  display: flex;
  align-items: center;

  /* Acrobatics to ensure that the date hugs the upper right corner. Kind of brittle */
  min-height: 4.5rem;
  justify-content: flex-end;


  h4 {
    flex: 1;
    min-width: 200px;
    padding: var(--space-xs) var(--space-lg);
    font-size: var(--font-size-xl);
    margin: 0;
    font-weight: 400;
  }

  .event-anchor {
    text-wrap: nowrap;
    font-size: var(--font-size-xs);
    padding: 0 var(--space-2xs);
  }
}

.calendar-date-range {
  font-family: var(--font-display);
  background-color: hsl(0 0% 0% / 70%);
  border-style: solid;

  /* It is meant to hug the upper right corner */
  border-width: 0px 0px 1px 1px;
  border-color: var(--color-border-primary);

  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  .calendar-date {
    /* Acrobatics to ensure that the date hugs the upper right corner. Kind of brittle */
    width: 4.5rem;
    height: 4.5rem;

    /* */
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: var(--font-size-sm);
    line-height: 1.3em;
    font-weight: bold;

    .date {
      font-size: var(--font-size-xl);
    }
  }

  .arrow {
    font-size: var(--font-size-lg);
    padding: 0 var(--space-2xs);
  }
}

.card-meta {
  padding: var(--space-md) var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  background: var(--color-bg-overlay-white-subtle);
  border-bottom: 1px solid var(--color-border-subtle);

  .meta-item {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
    font-size: var(--font-size-sm);
  }

  .meta-icon {
    width: 16px;
    font-size: var(--font-size-xs);
  }

  .meta-text {
    font-style: italic;
    color: var(--color-text-white-90);
  }

  .meta-link {
    font-style: italic;
  }
}

.card-body {
  padding: var(--space-lg);

  p {
    white-space: pre-line;
  }
}

.card-banner {
  position: relative;
  width: 100%;
  height: 274px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .credit {
    font-size: 10px;
    position: absolute;
    bottom: 0;
    left: 0;
    background-color: hsla(0, 0%, 0%, 0.5);
    padding: 0.1em 0.4em;
    border-radius: 0 4px 0 0;
    opacity: 0.5;
    transition: opacity 100ms;
  }

  &:hover .credit {
    opacity: 1;
  }
}
</style>
